import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { APPLICATION_STATUS, getStatusColor, getStatusIcon, formatDate, formatCurrency, getCompanyName } from '../helpers';

/**
 * Application Card Component
 * Displays individual application information in list or grid layout
 */
const ApplicationCard = ({ 
  application, 
  onViewJob, 
  onContactEmployer, 
  onViewDetails,
  onArchive,
  contactingEmployer,
  layout = 'list'
}) => {
  const handleClick = (e, callback) => {
    e.stopPropagation();
    callback();
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-sm border border-border-primary p-6 hover:shadow-md transition-shadow cursor-pointer relative"
      onClick={() => onViewDetails(application)}
    >
      {/* Archive Button - Top Right */}
      <button
        onClick={(e) => handleClick(e, () => onArchive?.(application))}        className="absolute top-3 right-3 p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-background-secondary transition-colors"
        title={application.is_archived ? 'Unarchive application' : 'Archive application'}
      >
        <Icon name={application.is_archived ? 'ArchiveRestore' : 'Archive'} size={16} />
      </button>

      <div className={`flex flex-col ${layout === 'list' ? 'lg:flex-row lg:items-center lg:justify-between' : ''} gap-4`}>
        {/* Job Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-1">
                {application.job?.title || 'Unknown Position'}
              </h3>
              <p className="text-text-secondary">
                {getCompanyName(application)}
              </p>
            </div>
            <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(application.status)}`}>
              <div className="flex items-center space-x-1">
                <Icon name={getStatusIcon(application.status)} size={14} />
                <span className="capitalize">{application.status}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-text-muted" />
              <span className="text-text-secondary">
                Applied: {formatDate(application.applied_at)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="DollarSign" size={16} className="text-text-muted" />
              <span className="text-text-secondary">
                Proposed: {formatCurrency(application.proposed_rate)}
              </span>
            </div>
          </div>

          {/* Cover Letter Preview */}
          {application.cover_letter && (
            <div className="mt-3 p-3 bg-background-secondary rounded-md">
              <p className="text-sm text-text-secondary line-clamp-2">
                <span className="font-medium">Cover Letter: </span>
                {application.cover_letter}
              </p>
            </div>
          )}

          {/* Interview Date */}
          {(application.interview_scheduled_at || application.interview_data?.scheduled_at) && (
            <div className="mt-3 p-3 bg-accent-50 border border-accent-200 rounded-md">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} className="text-accent" />
                <span className="text-sm font-medium text-accent">
                  Interview: {formatDate(application.interview_scheduled_at || application.interview_data.scheduled_at)}
                  {application.interview_data?.feedback?.recommendation && (
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                      application.interview_data.feedback.recommendation === 'hire' 
                        ? 'bg-success-100 text-success' 
                        : 'bg-warning-100 text-warning'
                    }`}>
                      {application.interview_data.feedback.recommendation}
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Hiring Offer Preview */}
          {application.status === APPLICATION_STATUS.HIRED && application.interview_data?.offer_amount && (
            <div className="mt-3 p-3 bg-success-50 border border-success-200 rounded-md">
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} className="text-success" />
                <span className="text-sm font-medium text-success">
                  Offer: {formatCurrency(application.interview_data.offer_amount)}
                  {application.interview_data.decision_date && 
                    ` • Decision: ${formatDate(application.interview_data.decision_date)}`}
                </span>
              </div>
            </div>
          )}

          {/* Employer Notes */}
          {application.employer_notes && (
            <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-md">
              <p className="text-sm text-primary">
                <span className="font-medium">Employer Note: </span>
                {application.employer_notes}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => handleClick(e, () => onViewJob(application))}
            iconName="ExternalLink"
          >
            View Job
          </Button>
          
          {(application.status === APPLICATION_STATUS.SHORTLISTED || 
            application.status === APPLICATION_STATUS.HIRED) && (
            <Button
              variant="success"
              size="sm"
              iconName="MessageSquare"
              onClick={(e) => handleClick(e, () => onContactEmployer(application))}
              disabled={contactingEmployer}
            >
              {contactingEmployer ? 'Connecting...' : 'Contact Employer'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ApplicationCard);
